import Localized from 'src/constants/AppStrings';
import {alertError} from './AlertHelper';
import MenuService from 'src/services/MenuService';
import TransactionStore from 'src/stores/TransactionStore';
import moment from 'moment';
import MenuActions from 'src/actions/MenuActions';
import TransactionActions from 'src/actions/TransactionActions';
import NavActions from 'src/actions/NavActions';
import AppRoutes from 'src/AppRoutes';
import Settings from 'src/Settings';
import Events from 'src/logging/Events';
import {generateErrorMessage} from 'src/logging/generateErrorMessage';
import uuid from '../../nativeModules/UUID';
import CrashlyticsEvents from '../../logging/Crashlytics';
import {PickUpTimeType, TimeSlotType} from 'src/types/Menu';
import {ScreenProps} from 'src/types/Screen';
import {LocationType} from 'src/types/Location';

export async function checkAndHandlePickedTimeSlot(
  context: ScreenProps,
  location: LocationType,
): Promise<boolean> {
  let timeSlotIsValid = true;
  if (!checkPickedTimeSlot(location)) {
    try {
      context.actions.showSpinner();
      const availableTimes = await MenuActions.getAvailableTimeSlots(
        location,
        TransactionStore.getPickupLocationId(),
      );
      timeSlotIsValid = false;
      handelPickupTimeNotAvailableError(availableTimes, location);
    } catch (error) {
      timeSlotIsValid = false;
      const guid = await uuid.getRandomUUID();
      CrashlyticsEvents.log(
        'Exception',
        'PickupTimeScreen:PickupTimeSelected',
        generateErrorMessage(error),
        guid,
      );
      Events.Error.trackEvent(
        'Exception',
        'PickupTimeScreen:PickupTimeSelected',
        generateErrorMessage(error),
        guid,
      );
    } finally {
      context.actions.hideSpinner();
    }
  }
  return Promise.resolve(timeSlotIsValid);
}

export function checkPickedTimeSlot(location: LocationType): boolean {
  if (!location || !location?.onlineOrderConfig) {
    return true;
  }

  const timeZoneOffsetMinutes = Number(
    MenuService.getTimezoneOffsetMinutes() ?? 0,
  );
  const leadTime = Number(
    (location?.onlineOrderConfig as any)?.kitchenSchedule ?? 0,
  );
  const pickupTime = TransactionStore.getPickupTime();

  if (!pickupTime || !pickupTime.date) {
    return true;
  }
  const now = moment()
    .add(leadTime, 'minutes')
    .add(timeZoneOffsetMinutes, 'minutes');

  return !(
    !moment(pickupTime.date).isAfter(now) &&
    pickupTime.time !== PickUpTimeType.ASAP
  );
}

export async function checkAndHandlePickupTime(
  log: string,
  callback?: () => void,
): Promise<boolean> {
  let pickupTimeIsValid = true;
  const pickupTime = TransactionStore.getPickupTime();
  if (!pickupTime || !pickupTime.date) {
    const guid = await uuid.getRandomUUID();
    CrashlyticsEvents.log(
      'Exception',
      log,
      'pickupTime or pickupTime.date is null or undefined',
      guid,
    );
    Events.Error.trackEvent(
      'Exception',
      log,
      'pickupTime or pickupTime.date is null or undefined',
      guid,
    );
    alertError(Localized.Errors.error_finalizing_order, guid, callback);
    pickupTimeIsValid = false;
  }

  return Promise.resolve(pickupTimeIsValid);
}

export function handleNotPresentTimeSlot(
  availableTimes: TimeSlotType[],
  location: LocationType,
) {
  alertError(
    Localized.Errors.order_limit_reached_for_pickup_time,
    undefined,
    () => {
      if (Settings.isNewUI()) {
        NavActions.replace(AppRoutes.NewPickupTime, {
          location: location,
          availableTimes: MenuService.getAvailableTimeSlotsFromList(
            availableTimes,
            location.onlineOrderConfig.kitchenSchedule,
          ),
          selectedPickupLocation: TransactionStore.getPickupLocation(),
        });
      } else {
        NavActions.replace(AppRoutes.PickupTime, {
          strings: Localized,
          location: location,
          availableTimes,
        });
      }
    },
  );
}

export function handelPickupTimeNotAvailableError(
  availableTimes: TimeSlotType[],
  location: LocationType,
) {
  alertError(Localized.Errors.pickup_time_not_available, undefined, () => {
    TransactionActions.shoppingCartTransactionsCleared();
    if (Settings.isNewUI()) {
      NavActions.navigate(AppRoutes.NewPickupTime, {
        availableTimes: MenuService.getAvailableTimeSlotsFromList(
          availableTimes,
          location.onlineOrderConfig.kitchenSchedule,
        ),
        location: location,
        selectedPickupLocation: TransactionStore.getPickupLocation(),
      });
    } else {
      NavActions.navigate(AppRoutes.PickupTime, {
        strings: Localized,
        location: location,
        availableTimes,
      });
    }
  });
}
